"use client";

import { CalendarDays, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";

export interface BookingDateOption {
  key: string;
  date: Date;
  disabled?: boolean;
  disabledReason?: string | null;
  ariaLabel?: string;
}

export interface BookingSlotOption {
  key: string;
  label: string;
  description?: string | null;
  disabled?: boolean;
  disabledReason?: string | null;
  ariaLabel?: string;
  /**
   * Time-first layout. When set, the chip leads with the time window
   * ("6:30 AM – 7:30 AM"), with `label` (the class name) — or `detail` when
   * there is no label — on one line under it. Without it the chip keeps the centred label/description layout the event page uses.
   */
  time?: {
    value: string;
    meridiem?: string | null;
    /** The window's end, rendered as "6:30 am – 7:30 am". */
    end?: { value: string; meridiem?: string | null } | null;
  } | null;
  detail?: string | null;
}

interface DateTimeSlotPickerProps {
  title?: string;
  dateOptions: BookingDateOption[];
  selectedDateKey: string | null;
  onDateChange: (dateKey: string) => void;
  onDisabledDateClick?: (option: BookingDateOption) => void;
  slotOptions: BookingSlotOption[];
  selectedSlotKey: string | null;
  onSlotChange: (slotKey: string) => void;
  onDisabledSlotClick?: (option: BookingSlotOption) => void;
  loading?: boolean;
  loadingLabel?: string;
  emptyLabel?: string;
  selectedDateEmptyLabel?: string;
  visibleSlotCount?: number;
  showCalendarIcon?: boolean;
  onCalendarClick?: () => void;
  slotHeader?: ReactNode;
  showSlots?: boolean;
  showDates?: boolean;
  /** See `BookingSlotGrid`'s `slotSize`. */
  slotSize?: SlotSize;
  className?: string;
  appearance?: "default" | "glass" | "solid";
}

interface BookingSlotGridProps {
  slotOptions: BookingSlotOption[];
  selectedSlotKey: string | null;
  onSlotChange: (slotKey: string) => void;
  onDisabledSlotClick?: (option: BookingSlotOption) => void;
  selectedDateKey?: string | null;
  loading?: boolean;
  loadingLabel?: string;
  selectedDateEmptyLabel?: string;
  visibleSlotCount?: number;
  slotHeader?: ReactNode;
  /**
   * `comfortable` for a grid of bare times (one class's own page, where the
   * class name under each time would only repeat the title): a taller chip
   * and a larger time, so a lone line still reads as a tappable card rather
   * than a pill. `compact` (default) for two-line chips.
   */
  slotSize?: SlotSize;
  className?: string;
  appearance?: "default" | "glass" | "solid";
}

type SlotSize = "compact" | "comfortable";

const MONTH_LABELS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
] as const;

const DAY_NUMBER_FORMATTER = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
});

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-IN", {
  weekday: "short",
});

function getMonthKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}`;
}

function groupDateOptions(dateOptions: BookingDateOption[]) {
  return dateOptions.reduce<
    Array<{ key: string; label: string; options: BookingDateOption[] }>
  >((groups, option) => {
    const key = getMonthKey(option.date);
    const existing = groups[groups.length - 1];

    if (existing?.key === key) {
      existing.options.push(option);
      return groups;
    }

    groups.push({
      key,
      label: MONTH_LABELS[option.date.getMonth()],
      options: [option],
    });
    return groups;
  }, []);
}

export function DateTimeSlotPicker({
  title = "Choose date and time",
  dateOptions,
  selectedDateKey,
  onDateChange,
  onDisabledDateClick,
  slotOptions,
  selectedSlotKey,
  onSlotChange,
  onDisabledSlotClick,
  loading = false,
  loadingLabel = "Loading availability...",
  emptyLabel = "No dates available.",
  selectedDateEmptyLabel = "No slots on this date.",
  visibleSlotCount = 6,
  showCalendarIcon = false,
  onCalendarClick,
  slotHeader,
  showSlots = true,
  showDates = true,
  slotSize,
  className,
  appearance = "default",
}: DateTimeSlotPickerProps) {
  const dateGroups = useMemo(
    () => groupDateOptions(dateOptions),
    [dateOptions],
  );

  return (
    <section
      className={cn("space-y-4 text-text-primary dark:text-white", className)}
    >
      {title ? (
        <h2 className="text-xl leading-tight font-semibold tracking-normal">
          {title}
        </h2>
      ) : null}

      {!showDates ? null : dateOptions.length === 0 ? (
        <p className="rounded-2xl border border-border bg-surface px-4 py-5 text-sm text-text-secondary dark:border-white/[0.1] dark:bg-transparent dark:text-white/58">
          {emptyLabel}
        </p>
      ) : (
        <div className="snap-x snap-mandatory [scroll-padding-inline-start:3rem] [scrollbar-width:none] overflow-x-auto">
          <div className="flex min-w-max items-center gap-2">
            {showCalendarIcon ? (
              onCalendarClick ? (
                <button
                  type="button"
                  aria-label="Open calendar"
                  className="sticky left-0 z-30 flex h-14 w-12 shrink-0 items-center justify-center rounded-xl border border-border bg-surface text-text-primary shadow-sm transition hover:bg-surface-alt dark:border-glass-border-08 dark:bg-[#17171d] dark:text-white dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] dark:hover:bg-white/[0.12]"
                  onClick={onCalendarClick}
                >
                  <CalendarDays className="h-5 w-5" strokeWidth={2.1} />
                </button>
              ) : (
                <div
                  aria-hidden
                  className="sticky left-0 z-30 flex h-14 w-12 shrink-0 items-center justify-center rounded-xl border border-border bg-surface text-text-primary shadow-sm dark:border-glass-border-08 dark:bg-[#17171d] dark:text-white dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                >
                  <CalendarDays className="h-5 w-5" strokeWidth={2.1} />
                </div>
              )
            ) : null}

            {dateGroups.map((group) => (
              <div key={group.key} className="contents">
                <div
                  className={cn(
                    "sticky z-20 flex h-14 w-10 shrink-0 items-center justify-center bg-background dark:bg-[#0b0b0f]",
                    showCalendarIcon ? "left-14" : "left-0",
                  )}
                >
                  <div className="flex h-14 w-10 items-center justify-center rounded-xl border border-border bg-surface-alt text-xs font-semibold tracking-normal text-text-secondary shadow-sm [writing-mode:vertical-rl] dark:border-transparent dark:bg-[#1f1f26] dark:text-white/80 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.06)]">
                    {group.label}
                  </div>
                </div>

                {group.options.map((option) => {
                  const selected = option.key === selectedDateKey;
                  const disabled = Boolean(option.disabled);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      disabled={disabled && !onDisabledDateClick}
                      aria-pressed={selected}
                      aria-disabled={disabled || undefined}
                      aria-label={option.ariaLabel}
                      className={cn(
                        "flex h-14 w-16 shrink-0 flex-col items-center justify-center rounded-xl border text-center transition",
                        "snap-start",
                        appearance === "solid"
                          ? selected
                            ? "border-brand bg-brand text-brand-foreground hover:bg-brand-hover dark:border-white dark:bg-white dark:text-black dark:hover:bg-white/92"
                            : "border-border bg-surface-alt text-text-primary hover:bg-surface-elevated"
                          : selected
                            ? "border-brand bg-surface text-text-primary shadow-sm dark:border-white dark:bg-[#f4f4f5] dark:text-[#0f0f12]"
                            : "border-border bg-surface/70 text-text-secondary hover:bg-surface hover:text-text-primary dark:border-glass-border-08 dark:bg-transparent dark:text-white/74 dark:hover:bg-glass-bg-06",
                        !selected &&
                          disabled &&
                          "cursor-not-allowed border-border/60 text-text-tertiary/55 opacity-60 hover:bg-surface/70 dark:border-white/[0.04] dark:text-white/24 dark:hover:bg-transparent",
                      )}
                      onClick={() => {
                        if (disabled) {
                          onDisabledDateClick?.(option);
                          return;
                        }
                        onDateChange(option.key);
                      }}
                    >
                      <span className="text-xl leading-none font-semibold">
                        {DAY_NUMBER_FORMATTER.format(option.date).padStart(
                          2,
                          "0",
                        )}
                      </span>
                      <span className="mt-1 text-[0.72rem] leading-none">
                        {WEEKDAY_FORMATTER.format(option.date)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {showSlots ? (
        <BookingSlotGrid
          appearance={appearance}
          selectedDateKey={selectedDateKey}
          slotOptions={slotOptions}
          selectedSlotKey={selectedSlotKey}
          onSlotChange={onSlotChange}
          onDisabledSlotClick={onDisabledSlotClick}
          loading={loading}
          loadingLabel={loadingLabel}
          selectedDateEmptyLabel={selectedDateEmptyLabel}
          visibleSlotCount={visibleSlotCount}
          slotHeader={slotHeader}
          slotSize={slotSize}
        />
      ) : null}
    </section>
  );
}

export function BookingSlotGrid({
  slotOptions,
  selectedSlotKey,
  onSlotChange,
  onDisabledSlotClick,
  selectedDateKey = null,
  loading = false,
  loadingLabel = "Loading availability...",
  selectedDateEmptyLabel = "No slots on this date.",
  visibleSlotCount = 6,
  slotHeader,
  slotSize = "compact",
  className,
  appearance = "default",
}: BookingSlotGridProps) {
  const [expandedState, setExpandedState] = useState<{
    dateKey: string | null;
    expanded: boolean;
  }>({ dateKey: selectedDateKey, expanded: false });
  const expanded =
    expandedState.dateKey === selectedDateKey && expandedState.expanded;

  const visibleSlots = useMemo(() => {
    if (expanded || slotOptions.length <= visibleSlotCount) {
      return slotOptions;
    }
    return slotOptions.slice(0, visibleSlotCount);
  }, [expanded, slotOptions, visibleSlotCount]);

  const canExpand = slotOptions.length > visibleSlotCount;

  return (
    <div className={cn("space-y-3", className)}>
      {slotHeader}

      {loading ? (
        <div className="rounded-2xl border border-border bg-surface px-4 py-5 text-center text-sm font-medium text-text-secondary dark:border-glass-border-08 dark:bg-transparent dark:text-white/58">
          {loadingLabel}
        </div>
      ) : slotOptions.length === 0 ? (
        <div className="rounded-2xl border border-border bg-surface px-4 py-5 text-center text-sm font-medium text-text-secondary dark:border-glass-border-08 dark:bg-transparent dark:text-white/58">
          {selectedDateEmptyLabel}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {visibleSlots.map((slot) => {
            const selected = slot.key === selectedSlotKey;
            const disabled = Boolean(slot.disabled);
            if (slot.time) {
              return (
                <TimeFirstSlotChip
                  key={slot.key}
                  slot={slot}
                  selected={selected}
                  disabled={disabled}
                  size={slotSize}
                  clickableWhenDisabled={Boolean(onDisabledSlotClick)}
                  onClick={() => {
                    if (disabled) {
                      onDisabledSlotClick?.(slot);
                      return;
                    }
                    onSlotChange(slot.key);
                  }}
                />
              );
            }
            return (
              <button
                key={slot.key}
                type="button"
                disabled={disabled && !onDisabledSlotClick}
                aria-pressed={selected}
                aria-disabled={disabled || undefined}
                aria-label={slot.ariaLabel}
                className={cn(
                  "flex min-h-15 min-w-0 flex-col items-center justify-center gap-0.5 rounded-2xl border px-2.5 py-2.5 text-center",
                  // Press feedback rather than hover lift: Capacitor taps leave
                  // a sticky false hover, so a tappable never scales on hover
                  // (see the motion tokens rule in globals.css).
                  "transition-[transform,background-color,border-color,box-shadow] duration-150 ease-out-strong",
                  appearance === "glass"
                    ? selected
                      ? "border-black/25 bg-white/80 text-text-primary ring-1 ring-black/10 backdrop-blur-xl backdrop-saturate-150 dark:border-white/45 dark:bg-white/18 dark:text-white dark:ring-white/15"
                      : "border-white/65 bg-white/45 text-text-primary shadow-sm backdrop-blur-xl backdrop-saturate-150 hover:bg-white/65 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                    : selected
                      ? "border-brand bg-brand text-brand-foreground shadow-sm dark:border-white dark:bg-white dark:text-black"
                      : "cursor-pointer border-border-strong bg-surface text-text-primary shadow-sm hover:bg-surface-alt active:scale-[0.97] dark:border-white/[0.16] dark:bg-glass-bg-05 dark:text-white dark:hover:border-white/[0.3] dark:hover:bg-glass-bg-08",
                  appearance === "solid" &&
                    (selected
                      ? "hover:bg-brand-hover dark:hover:bg-white/92"
                      : "bg-surface-alt hover:bg-surface-elevated dark:bg-surface-alt dark:hover:bg-surface-elevated"),
                  disabled &&
                    "cursor-not-allowed border-border/60 bg-surface text-text-tertiary/55 opacity-60 shadow-none hover:bg-surface active:scale-100 dark:border-white/[0.07] dark:bg-transparent dark:text-white/24 dark:hover:bg-transparent",
                )}
                onClick={() => {
                  if (disabled) {
                    onDisabledSlotClick?.(slot);
                    return;
                  }
                  onSlotChange(slot.key);
                }}
              >
                <span
                  title={slot.label}
                  className={cn(
                    "w-full text-[0.9375rem] leading-tight font-semibold tabular-nums",
                    appearance === "glass" &&
                      "truncate text-[0.8125rem] sm:text-[0.9375rem]",
                  )}
                >
                  {slot.label}
                </span>
                {slot.description ? (
                  <span
                    className={cn(
                      "w-full text-[0.72rem] leading-tight tabular-nums",
                      selected ? "opacity-75" : "opacity-60",
                    )}
                  >
                    {slot.description}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {canExpand ? (
        <button
          type="button"
          className="mx-auto flex items-center gap-1.5 px-2 text-sm leading-none font-semibold text-text-secondary transition hover:text-text-primary dark:text-white/76 dark:hover:text-white"
          onClick={() =>
            setExpandedState({
              dateKey: selectedDateKey,
              expanded: !expanded,
            })
          }
        >
          {expanded ? "Show fewer slots" : "View all slots"}
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              expanded && "rotate-180",
            )}
            strokeWidth={2.5}
          />
        </button>
      ) : null}
    </div>
  );
}

/**
 * A slot chip that leads with WHEN: the full window ("6:30 AM – 7:30 AM"),
 * centred and bold like a native time picker, with the class name on one
 * quiet line under it when there is one. Kept short on purpose — a day of
 * seven classes should scan as a list of times, not a wall of cards.
 */
function TimeFirstSlotChip({
  slot,
  selected,
  disabled,
  size,
  clickableWhenDisabled,
  onClick,
}: {
  slot: BookingSlotOption;
  selected: boolean;
  disabled: boolean;
  size: SlotSize;
  clickableWhenDisabled: boolean;
  onClick: () => void;
}) {
  const time = slot.time;
  if (!time) return null;
  // Same weight and colour as the digits: "6:30 AM" is one token to the eye.
  const meridiemClass = "ml-[0.2em] uppercase";
  const secondary = slot.label || slot.detail;

  return (
    <button
      type="button"
      disabled={disabled && !clickableWhenDisabled}
      aria-pressed={selected}
      aria-disabled={disabled || undefined}
      aria-label={slot.ariaLabel}
      title={slot.ariaLabel}
      className={cn(
        "flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl border px-2 text-center",
        size === "comfortable" ? "min-h-15 py-3" : "py-2",
        // Press feedback rather than hover lift: Capacitor taps leave a
        // sticky false hover, so a tappable never scales on hover.
        "transition-[transform,background-color,border-color,box-shadow] duration-150 ease-out-strong",
        selected
          ? "border-brand bg-brand text-brand-foreground shadow-sm dark:border-white dark:bg-white dark:text-black"
          : "cursor-pointer border-border-strong bg-surface text-text-primary shadow-sm hover:bg-surface-alt active:scale-[0.97] dark:border-white/[0.16] dark:bg-glass-bg-05 dark:text-white dark:hover:border-white/[0.3] dark:hover:bg-glass-bg-08",
        disabled &&
          "cursor-not-allowed border-border/60 bg-surface text-text-tertiary/55 opacity-60 shadow-none hover:bg-surface active:scale-100 dark:border-white/[0.07] dark:bg-transparent dark:text-white/24 dark:hover:bg-transparent",
      )}
      onClick={onClick}
    >
      <span
        className={cn(
          "leading-tight font-bold tracking-tight whitespace-nowrap tabular-nums",
          size === "comfortable" ? "text-[15px]" : "text-sm",
          disabled && "line-through decoration-1",
        )}
      >
        {time.value}
        {time.meridiem ? (
          <span className={meridiemClass}>{time.meridiem}</span>
        ) : null}
        {time.end ? (
          <>
            <span className="mx-1">–</span>
            {time.end.value}
            {time.end.meridiem ? (
              <span className={meridiemClass}>{time.end.meridiem}</span>
            ) : null}
          </>
        ) : null}
      </span>
      {secondary ? (
        <span
          className={cn(
            "w-full truncate text-xs leading-tight",
            selected ? "opacity-75" : "opacity-60",
          )}
        >
          {secondary}
        </span>
      ) : null}
    </button>
  );
}
